import React from 'react';
import { useTranslation } from 'react-i18next';

const ManualPage: React.FC = () => {
  const { t } = useTranslation('translation');

  const getSafeArray = (key: string): any[] => {
    const result = t(key, { returnObjects: true });
    return Array.isArray(result) ? result : [];
  };

  const sections = [
    { key: 'gettingStarted', title: t('queue.manual.sections.gettingStarted.title') },
    { key: 'dashboard', title: t('queue.manual.sections.dashboard.title') },
    { key: 'queueManagement', title: t('queue.manual.sections.queueManagement.title') },
    { key: 'settings', title: t('queue.manual.sections.settings.title') },
    { key: 'analytics', title: t('queue.manual.sections.analytics.title') },
    { key: 'customerPages', title: t('queue.manual.sections.customerPages.title') },
  ];

  const scrollToSection = (sectionKey: string) => {
    const element = document.getElementById(sectionKey);
    if (element) {
      const offsetPosition = element.offsetTop - 30;
      window.scrollTo({ top: offsetPosition, behavior: 'auto' });
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
          {t('queue.manual.title')}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
        {/* 側邊目錄 */}
        <div className="lg:col-span-1">
          <div className="sticky top-8">
            <nav className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
                {t('queue.manual.tableOfContents')}
              </h2>
              <ul className="space-y-2">
                {sections.map((section) => (
                  <li key={section.key}>
                    <button
                      onClick={() => scrollToSection(section.key)}
                      className="text-left w-full text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 hover:underline"
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        {/* 主要內容 */}
        <div className="lg:col-span-5">

          {/* 快速入門 */}
          <section id="gettingStarted" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.gettingStarted.title')}
            </h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                {t('queue.manual.sections.gettingStarted.welcome.title')}
              </h3>
              <p className="text-blue-800 dark:text-blue-200">
                {t('queue.manual.sections.gettingStarted.welcome.description')}
              </p>
            </div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              {t('queue.manual.sections.gettingStarted.features.title')}
            </h4>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
              {getSafeArray('queue.manual.sections.gettingStarted.features.items').map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* 儀表板 */}
          <section id="dashboard" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.dashboard.title')}
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('queue.manual.sections.dashboard.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                  {t('queue.manual.sections.dashboard.cards.currentServing.title')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('queue.manual.sections.dashboard.cards.currentServing.description')}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                  {t('queue.manual.sections.dashboard.cards.todaySummary.title')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('queue.manual.sections.dashboard.cards.todaySummary.description')}
                </p>
              </div>
            </div>
          </section>

          {/* 叫號管理 */}
          <section id="queueManagement" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.queueManagement.title')}
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('queue.manual.sections.queueManagement.description')}
            </p>
            <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              {t('queue.manual.sections.queueManagement.features.title')}
            </h4>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
              {getSafeArray('queue.manual.sections.queueManagement.features.items').map((item: any, index: number) => (
                <li key={index}>
                  <strong>{item.title}</strong>{item.description}
                </li>
              ))}
            </ul>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                {t('queue.manual.sections.queueManagement.tip.title')}
              </h4>
              <p className="text-green-800 dark:text-green-200 text-sm">
                {t('queue.manual.sections.queueManagement.tip.content')}
              </p>
            </div>
          </section>

          {/* 排隊設定 */}
          <section id="settings" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.settings.title')}
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('queue.manual.sections.settings.description')}
            </p>
            <div className="space-y-4 mb-6">
              {['storeInfo', 'queueParams', 'switches', 'qrCode'].map((configKey) => (
                <div key={configKey} className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                    {t(`queue.manual.sections.settings.configurations.${configKey}.title`)}
                  </h5>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    {getSafeArray(`queue.manual.sections.settings.configurations.${configKey}.items`).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t('queue.manual.sections.settings.tip.title')}
              </h4>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                {t('queue.manual.sections.settings.tip.content')}
              </p>
            </div>
          </section>

          {/* 分析報表 */}
          <section id="analytics" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.analytics.title')}
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('queue.manual.sections.analytics.description')}
            </p>
            <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              {t('queue.manual.sections.analytics.features.title')}
            </h4>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
              {getSafeArray('queue.manual.sections.analytics.features.items').map((item: any, index: number) => (
                <li key={index}>
                  <strong>{item.title}</strong>{item.description}
                </li>
              ))}
            </ul>
          </section>

          {/* 客人頁面 */}
          <section id="customerPages" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('queue.manual.sections.customerPages.title')}
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('queue.manual.sections.customerPages.description')}
            </p>
            <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
              {t('queue.manual.sections.customerPages.features.title')}
            </h4>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
              {getSafeArray('queue.manual.sections.customerPages.features.items').map((item: any, index: number) => (
                <li key={index}>
                  <strong>{item.title}</strong>{item.description}
                </li>
              ))}
            </ul>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t('queue.manual.sections.customerPages.tip.title')}
              </h4>
              <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                {getSafeArray('queue.manual.sections.customerPages.tip.items').map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ManualPage;
